// P2-25 command-channel poller (docs/design/P2-25-command-channel-contract.md §5).
// Framework-agnostic: no Pinia/router/auth-store imports, so it's unit-testable in complete
// isolation and buildable ahead of T5 (which wires `start()`'s handlers to the real auth
// store / router / query cache and calls `start()`/`stop()` from login/logout).
//
// Owns: cadence + jitter (§5.1), backoff (§5.2), page-visibility/online pause-resume (§5.3),
// and per-element command dispatch + batch ack (§5.4). Does NOT own: what a handler actually
// DOES (that's the `handlers` object passed into `start()` — a T5 concern) or session
// bootstrap (the `refresh` hook passed into `start()` is called, never implemented, here).
import {
  dataChangedPayloadSchema,
  forceLogoutPayloadSchema,
  jobStatusPayloadSchema,
  reloadPayloadSchema,
  userCommandSchema,
  type UserCommand,
} from '@eskayscience/shared/dto';
import { CommandType } from '@eskayscience/shared/enums';
import type { z } from 'zod';

import { ApiError, NetworkError, commandsApi, type CommandsApi } from './api';

/** `supersededAt` is not part of the server-sent `forceLogoutPayloadSchema` (§4 command
 * channel contract) — it only ever arrives on the §6.2 poll-401 `SESSION_SUPERSEDED` mapping
 * below, lifted straight from that response's `ProblemDetails.supersededAt` (session-superseded-
 * UX task). Optional here so a real FORCE_LOGOUT command (which never carries it) still
 * type-checks unchanged. */
export type ForceLogoutPayload = z.infer<typeof forceLogoutPayloadSchema> & {
  supersededAt?: string;
};
export type JobStatusPayload = z.infer<typeof jobStatusPayloadSchema>;
export type ReloadPayload = z.infer<typeof reloadPayloadSchema>;
export type DataChangedPayload = z.infer<typeof dataChangedPayloadSchema>;

/** The four §5.4 dispatch handlers, plus one transport-level "give up" hook. Deliberately
 * pluggable (not imported from `../stores/auth` here) — T5 supplies the real implementations
 * (stop poller / clear token / redirect / invalidate caches) when it wires `start()` into
 * login/logout. Every handler is invoked in its own try/catch by the poller (§5.4: "one
 * throwing handler must never prevent the rest of the batch or the ack"), so a handler here
 * may throw freely without affecting anything else. */
export interface CommandDispatchHandlers {
  /** `commandId` is `null` when this is the §6.2 synthesized mapping of a `401
   * SESSION_SUPERSEDED` poll response onto the same handler a real `FORCE_LOGOUT` command
   * would trigger — there is no real command row to ack in that case. */
  onForceLogout?(payload: ForceLogoutPayload, commandId: string | null): void | Promise<void>;
  onJobStatus?(payload: JobStatusPayload, commandId: string): void | Promise<void>;
  onReload?(payload: ReloadPayload, commandId: string): void | Promise<void>;
  onDataChanged?(payload: DataChangedPayload, commandId: string): void | Promise<void>;
  /** Called when the poller gives up after `401 AUTH_TOKEN_EXPIRED` + one failed silent
   * refresh (§5.2: "If that fails → stop + teardown. Never loop."). Not a §5.4 command — a
   * transport-level "the session is gone" signal. T5 should treat it like a FORCE_LOGOUT
   * teardown (the poller has already stopped itself by the time this fires). */
  onAuthExpired?(): void | Promise<void>;
}

export interface CommandPollerDeps {
  api?: CommandsApi;
  /** Attempt exactly one silent token refresh on `401 AUTH_TOKEN_EXPIRED` (§5.2). Returns
   * `true` if a new access token is now in place and the poll should be retried once.
   * Omitted (or itself failing) means the poller gives up immediately and calls
   * `handlers.onAuthExpired`. Deliberately a plain function, not `authStore.refresh` imported
   * directly — keeps this module decoupled from the auth store (T5 supplies it). */
  refresh?: () => Promise<boolean>;
}

const BASE_INTERVAL_MS = 25_000;
const MIN_INTERVAL_MS = 5_000;
const MAX_INTERVAL_MS = 300_000;
const MIN_GAP_MS = 5_000;
const BACKOFF_CAP_MS = 300_000;

/** `clamp(nextPollMs, 5000, 300000)`; an absent/invalid (non-finite / non-positive) value
 * falls back to `25000` (§5.1). */
function clampNextPollMs(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return BASE_INTERVAL_MS;
  return Math.min(MAX_INTERVAL_MS, Math.max(MIN_INTERVAL_MS, value));
}

/** ±20% full jitter on the base cadence (§5.1): actual range ≈ base×0.8 .. base×1.2. */
function jitterCadence(baseMs: number): number {
  return baseMs * (0.8 + Math.random() * 0.4);
}

/** AWS-style "full jitter" for backoff (§5.2: "with full jitter") — uniform over
 * `[0, capMs]`, capMs = `min(25s × 2^f, 5min)`. */
function backoffDelay(consecutiveFailures: number): number {
  const cap = Math.min(BASE_INTERVAL_MS * 2 ** consecutiveFailures, BACKOFF_CAP_MS);
  return Math.random() * cap;
}

function extractPossibleId(item: unknown): string | null {
  if (item && typeof item === 'object' && 'id' in item) {
    const id = (item as { id?: unknown }).id;
    if (typeof id === 'string' && id.length > 0) return id;
  }
  return null;
}

/** Parses each raw command element individually against `userCommandSchema` — never a
 * whole-array `.parse()` (§4 forward-compat note). Elements that fail are skipped (dropped),
 * logged, and — when an `id` can still be salvaged from the malformed shape — queued for ack
 * anyway so a poison row doesn't wedge the server-side queue for this client either. */
function parseCommands(raw: unknown[]): { valid: UserCommand[]; poisonIds: string[] } {
  const valid: UserCommand[] = [];
  const poisonIds: string[] = [];
  for (const item of raw) {
    const result = userCommandSchema.safeParse(item);
    if (result.success) {
      valid.push(result.data);
      continue;
    }
    const maybeId = extractPossibleId(item);
    if (maybeId) poisonIds.push(maybeId);
    console.warn('[command-poll] dropping malformed command element', {
      id: maybeId,
      issues: result.error.issues,
    });
  }
  return { valid, poisonIds };
}

type ConnectionState = 'idle' | 'polling' | 'backoff' | 'stopped';

export class CommandPoller {
  private readonly api: CommandsApi;
  private handlers: CommandDispatchHandlers = {};
  private deps: CommandPollerDeps = {};

  private timer: ReturnType<typeof setTimeout> | null = null;
  private scheduledFor = 0;
  private inFlight = false;
  private stopped = true;
  private consecutiveFailures = 0;
  private lastPollStartedAt = 0;
  private baseIntervalMs = BASE_INTERVAL_MS;
  private authExpiredRefreshAttempted = false;

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      this.clearTimer();
    } else {
      this.requestImmediatePoll();
    }
  };

  private readonly onOnline = (): void => {
    this.requestImmediatePoll();
  };

  constructor(api: CommandsApi = commandsApi) {
    this.api = api;
  }

  get connectionState(): ConnectionState {
    if (this.stopped) return 'stopped';
    if (this.consecutiveFailures > 0) return 'backoff';
    return this.inFlight || this.timer !== null ? 'polling' : 'idle';
  }

  get isRunning(): boolean {
    return !this.stopped;
  }

  /** Begin polling: registers visibility/online listeners and polls once immediately
   * (§5.1). Calling `start()` while already running restarts cleanly (stops first). */
  start(handlers: CommandDispatchHandlers, deps: CommandPollerDeps = {}): void {
    if (!this.stopped) this.stop();
    this.handlers = handlers;
    this.deps = deps;
    this.stopped = false;
    this.consecutiveFailures = 0;
    this.baseIntervalMs = BASE_INTERVAL_MS;
    this.lastPollStartedAt = 0;
    this.scheduledFor = 0;
    // §5.2 latch is per-poll-session bookkeeping, not global: `commandPoller` is a shared
    // singleton, so without resetting this here a stale `true` from a previous session (e.g.
    // logout → login) would make the NEXT session's first AUTH_TOKEN_EXPIRED skip the refresh
    // attempt and immediately force-logout.
    this.authExpiredRefreshAttempted = false;
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onOnline);
    }
    this.requestImmediatePoll();
  }

  /** Stop polling permanently (until the next `start()`): clears the timer, removes
   * listeners, and drops any in-flight-poll bookkeeping. Idempotent. */
  stop(): void {
    this.stopped = true;
    this.clearTimer();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
    }
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.scheduledFor = 0;
  }

  private scheduleAt(targetTime: number): void {
    if (this.stopped) return;
    // Coalesce: if something is already scheduled at or before the requested time, keep it —
    // avoids visibility-flap / hasMore churn from creating redundant timers.
    if (this.timer !== null && this.scheduledFor <= targetTime) return;
    this.clearTimer();
    const delay = Math.max(0, targetTime - Date.now());
    this.scheduledFor = targetTime;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.scheduledFor = 0;
      void this.poll();
    }, delay);
  }

  /** Poll as soon as the 5s minimum gap since the last poll allows (§5.1/§5.3). Used by
   * start(), visibility→visible, `online`, and hasMore re-poll. */
  private requestImmediatePoll(): void {
    if (this.stopped) return;
    const earliestAllowed = this.lastPollStartedAt + MIN_GAP_MS;
    this.scheduleAt(Math.max(Date.now(), earliestAllowed));
  }

  private scheduleNextCadence(): void {
    const jittered = jitterCadence(this.baseIntervalMs);
    const delay = Math.max(MIN_GAP_MS, jittered);
    this.scheduleAt(Date.now() + delay);
  }

  private scheduleBackoff(retryAfterMs: number | null): void {
    const delay = Math.max(MIN_GAP_MS, retryAfterMs ?? backoffDelay(this.consecutiveFailures));
    this.scheduleAt(Date.now() + delay);
  }

  /** Public entry point (timer tick / immediate-poll triggers): enforces the "only one
   * in-flight poll at a time" guard, then delegates to {@link attemptPoll}. The
   * `AUTH_TOKEN_EXPIRED` retry-once path calls `attemptPoll()` directly (see
   * {@link handleError}) so the retry happens WITHIN this same in-flight session rather than
   * being silently dropped by this guard. */
  private async poll(): Promise<void> {
    if (this.stopped || this.inFlight) return;
    this.inFlight = true;
    try {
      await this.attemptPoll();
    } finally {
      this.inFlight = false;
    }
  }

  private async attemptPoll(): Promise<void> {
    this.lastPollStartedAt = Date.now();
    try {
      const res = await this.api.poll();
      this.consecutiveFailures = 0;
      this.baseIntervalMs = clampNextPollMs(res.nextPollMs);
      const { valid, poisonIds } = parseCommands(res.commands);
      await this.processCommands(valid, poisonIds);
      if (this.stopped) return; // FORCE_LOGOUT (or its 401 mapping) stopped us mid-batch.
      if (res.hasMore) {
        this.requestImmediatePoll();
      } else {
        this.scheduleNextCadence();
      }
    } catch (err) {
      await this.handleError(err);
    }
  }

  private async ackBestEffort(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    try {
      // §3.2 caps a single ack batch at 50 ids; poll responses are already capped at 50, but
      // poison ids can add to that, so chunk defensively.
      for (let i = 0; i < ids.length; i += 50) {
        await this.api.ack(ids.slice(i, i + 50));
      }
    } catch (err) {
      // Best-effort (§5.4): a failed ack is logged and left to at-least-once redelivery,
      // never retried in a tight loop.
      console.warn('[command-poll] ack failed, leaving for redelivery', err);
    }
  }

  private async processCommands(commands: UserCommand[], poisonIds: string[]): Promise<void> {
    const pendingAck: string[] = [...poisonIds];
    for (const cmd of commands) {
      if (cmd.type === CommandType.FORCE_LOGOUT) {
        // Ack ordering exception (§3.3/§5.4): ack BEFORE teardown, since teardown destroys
        // the access token the ack call needs.
        await this.ackBestEffort([...pendingAck, cmd.id]);
        pendingAck.length = 0;
        await this.dispatchForceLogout(cmd.payload, cmd.id);
        // The session is gone — no point processing/acking anything after it in this batch.
        return;
      }
      await this.dispatchOne(cmd);
      pendingAck.push(cmd.id);
    }
    await this.ackBestEffort(pendingAck);
  }

  private async dispatchOne(cmd: UserCommand): Promise<void> {
    try {
      switch (cmd.type) {
        case CommandType.JOB_STATUS:
          await this.handlers.onJobStatus?.(cmd.payload, cmd.id);
          break;
        case CommandType.RELOAD:
          await this.handlers.onReload?.(cmd.payload, cmd.id);
          break;
        case CommandType.DATA_CHANGED:
          await this.handlers.onDataChanged?.(cmd.payload, cmd.id);
          break;
        default:
          // Exhaustive per the current v1 union; a future 5th type is already filtered out
          // by parseCommands' per-element safeParse before reaching here.
          break;
      }
    } catch (err) {
      // §5.4: "one throwing handler must never prevent the rest of the batch or the ack."
      console.error(`[command-poll] handler threw for ${cmd.type} ${cmd.id}`, err);
    }
  }

  private async dispatchForceLogout(
    payload: ForceLogoutPayload,
    commandId: string | null,
  ): Promise<void> {
    // Infrastructure-level stop, regardless of what the injected handler does — a FORCE_LOGOUT
    // must never leave the poller running.
    this.stop();
    try {
      await this.handlers.onForceLogout?.(payload, commandId);
    } catch (err) {
      console.error('[command-poll] onForceLogout handler threw', err);
    }
  }

  private async handleError(err: unknown): Promise<void> {
    if (err instanceof ApiError) {
      if (err.status === 401 && err.code === 'SESSION_SUPERSEDED') {
        // §6.2: the poll's own 401 SESSION_SUPERSEDED IS the eviction signal — map it onto
        // the same handler a real FORCE_LOGOUT command would trigger. Never attempt refresh.
        // `supersededAt` (session-superseded-UX task) is lifted straight off this response's
        // ProblemDetails, if present, so the handler can render a human-readable "signed in
        // elsewhere at <time>" message instead of the bare reason code; older/absent values
        // fall through as `undefined` and the handler falls back to its generic copy.
        await this.dispatchForceLogout(
          { reason: 'SESSION_SUPERSEDED', supersededAt: err.problem?.supersededAt },
          null,
        );
        return;
      }
      if (err.status === 401 && err.code === 'AUTH_TOKEN_EXPIRED') {
        if (!this.authExpiredRefreshAttempted) {
          this.authExpiredRefreshAttempted = true;
          let refreshed: boolean;
          try {
            refreshed = (await this.deps.refresh?.()) ?? false;
          } catch (refreshErr) {
            if (refreshErr instanceof NetworkError) {
              // A transient network/offline blip while attempting the refresh is NOT a
              // genuine auth rejection — don't force a teardown for it. Let the retry
              // attempt happen again on the next AUTH_TOKEN_EXPIRED (the flag below stays
              // set for THIS poll only; fall through to the standard network-error backoff
              // path instead of tearing down the session).
              this.authExpiredRefreshAttempted = false;
              this.consecutiveFailures += 1;
              console.warn(
                '[command-poll] refresh attempt hit a network error, backing off',
                refreshErr,
              );
              this.scheduleBackoff(null);
              return;
            }
            // Any other unexpected throw from `refresh` — treat like a definitive failure
            // (falls through to teardown below), consistent with `deps.refresh` resolving
            // `false`.
            refreshed = false;
          }
          if (refreshed) {
            this.authExpiredRefreshAttempted = false;
            // Retry once, immediately, outside the normal cadence/backoff — call the inner
            // attempt directly (not `poll()`) since we're still inside the original poll's
            // in-flight session; going through `poll()`'s guard here would no-op.
            await this.attemptPoll();
            return;
          }
        }
        this.stop();
        try {
          await this.handlers.onAuthExpired?.();
        } catch (handlerErr) {
          console.error('[command-poll] onAuthExpired handler threw', handlerErr);
        }
        return;
      }
      if (err.status === 429) {
        this.consecutiveFailures += 1;
        const retryAfterMs = err.retryAfterSeconds != null ? err.retryAfterSeconds * 1000 : null;
        console.warn('[command-poll] rate limited, backing off', { retryAfterMs });
        this.scheduleBackoff(retryAfterMs);
        return;
      }
      // Any other non-2xx (5xx, or an unexpected 4xx) — treat like the network-error row of
      // the §5.2 table: exponential backoff with full jitter, capped at 5 min.
      this.consecutiveFailures += 1;
      console.warn('[command-poll] poll failed, backing off', {
        status: err.status,
        code: err.code,
      });
      this.scheduleBackoff(null);
      return;
    }
    if (err instanceof NetworkError) {
      this.consecutiveFailures += 1;
      console.warn('[command-poll] network error, backing off', err);
      this.scheduleBackoff(null);
      return;
    }
    // Any other unexpected throw (should not happen — the transport's envelope schema never
    // throws, and per-element parsing never throws) — still must never crash the loop.
    this.consecutiveFailures += 1;
    console.error('[command-poll] unexpected error, backing off', err);
    this.scheduleBackoff(null);
  }
}

/** Shared instance for app-wide use (T5 imports this and calls `start()`/`stop()` from the
 * auth store's login/logout paths and `boot/index.ts`). Tests should construct their own
 * `new CommandPoller(mockApi)` instead of sharing this singleton. */
export const commandPoller = new CommandPoller();
